import { useState, useEffect, useCallback } from "react";
import {
  LayoutDashboard, Clock,
  Building2, CheckCircle2,
} from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import { useAuth } from "../../hooks/useAuth";
import { supabase } from "../../lib/supabaseClient";

const NAV = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/valet/dashboard" },
  { label: "History",   icon: Clock, path: "/valet/history", active: true },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(dateStr) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  });
}

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function getDateRange(range) {
  const today = new Date();
  const from  = new Date();
  if (range === "7")  from.setDate(today.getDate() - 6);
  if (range === "30") from.setDate(today.getDate() - 29);
  if (range === "90") from.setDate(today.getDate() - 89);
  return {
    from: from.toISOString().split("T")[0],
    to:   today.toISOString().split("T")[0],
  };
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const config = {
    completed:  { label: "Completed",  color: "var(--g600)", bg: "var(--g50)",  border: "var(--g200)" },
    on_site:    { label: "On Site",    color: "#1565c0",     bg: "#e3f2fd",     border: "#90caf9" },
    on_the_way: { label: "On The Way", color: "#e65100",     bg: "#fff3e0",     border: "#ffb74d" },
    idle:       { label: "Idle",       color: "var(--mut)",  bg: "#f5f5f5",     border: "#e0e0e0" },
  };
  const c = config[status] || config.idle;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase",
      padding: "3px 10px", borderRadius: 20,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
    }}>{c.label}</span>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ValetHistory() {
  const { profile } = useAuth();
  const valetCodeId = profile?.codeId;
  const valetLabel  = profile?.label || profile?.code || "Valet";

  const [services,   setServices]   = useState([]);
  const [propMap,    setPropMap]    = useState({});
  const [loading,    setLoading]    = useState(true);
  const [range,      setRange]      = useState("30");
  const [totalDone,  setTotalDone]  = useState(0);

  // Fetch properties map
  useEffect(() => {
    supabase.from("properties").select("id, name")
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(p => { map[p.id] = p.name; });
        setPropMap(map);
      });
  }, []);

  const fetchHistory = useCallback(async () => {
    if (!valetCodeId) return;
    setLoading(true);

    const { from, to } = getDateRange(range);

    const { data } = await supabase
      .from("valet_status")
      .select("id, property_id, status, service_date, updated_at")
      .eq("valet_code_id", valetCodeId)
      .gte("service_date", from)
      .lte("service_date", to)
      .order("service_date", { ascending: false });

    const records = data || [];
    setServices(records);
    setTotalDone(records.filter(r => r.status === "completed").length);
    setLoading(false);
  }, [valetCodeId, range]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const ranges = [
    { key: "7",  label: "Last 7 Days" },
    { key: "30", label: "Last 30 Days" },
    { key: "90", label: "Last 90 Days" },
  ];

  return (
    <DashboardLayout role="valet" title="Service History" navItems={NAV}>

      {/* Header */}
      <div className="fu" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#e65100", marginBottom: 6 }}>
          Valet Portal
        </div>
        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 500, color: "var(--ink)", margin: 0 }}>
          Service History
        </h2>
        <p style={{ fontSize: 13, color: "var(--mut)", marginTop: 4 }}>
          Logged in as <strong style={{ color: "#e65100" }}>{valetLabel}</strong>
        </p>
      </div>

      {/* Summary + Range Toggle */}
      <div className="fu d1" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        {/* Stats */}
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ background: "var(--g50)", border: "1.5px solid var(--g200)", borderRadius: 12, padding: "12px 18px", textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: "var(--g600)", fontFamily: "'Outfit',sans-serif", lineHeight: 1 }}>{totalDone}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--g600)", textTransform: "uppercase", letterSpacing: ".5px", marginTop: 4 }}>Completed</div>
          </div>
          <div style={{ background: "#fff", border: "1.5px solid var(--ln)", borderRadius: 12, padding: "12px 18px", textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: "var(--ink)", fontFamily: "'Outfit',sans-serif", lineHeight: 1 }}>{services.length}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--mut)", textTransform: "uppercase", letterSpacing: ".5px", marginTop: 4 }}>Total Services</div>
          </div>
        </div>

        {/* Range Toggle */}
        <div style={{ display: "flex", background: "#f5f5f5", borderRadius: 10, padding: 3, gap: 3 }}>
          {ranges.map(({ key, label }) => (
            <button key={key} onClick={() => setRange(key)} style={{
              padding: "7px 12px", borderRadius: 8, border: "none",
              background: range === key ? "#fff" : "transparent",
              color: range === key ? "var(--ink)" : "var(--mut)",
              fontSize: 12, fontWeight: 700, cursor: "pointer",
              boxShadow: range === key ? "0 1px 4px rgba(0,0,0,.08)" : "none",
              transition: "all .15s",
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Services List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "var(--mut)", fontSize: 13 }}>
          Loading history…
        </div>
      ) : services.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "40px 24px",
          background: "#fff", borderRadius: 14, border: "1.5px dashed var(--ln)",
        }}>
          <Clock size={30} color="var(--ln)" style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", marginBottom: 6 }}>No services found</div>
          <div style={{ fontSize: 13, color: "var(--mut)" }}>No service history for selected period.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {services.map((s, i) => {
            const propName = propMap[s.property_id] || "—";
            const isFirst  = i === 0;

            return (
              <div key={s.id} style={{
                background: "#fff", borderRadius: 12,
                border: `1.5px solid ${s.status === "completed" ? "var(--g200)" : "var(--ln)"}`,
                padding: "14px 16px",
                display: "flex", alignItems: "center", gap: 12,
                boxShadow: "0 1px 4px rgba(0,0,0,.04)",
              }}>
                {/* Icon */}
                <div style={{
                  width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                  background: s.status === "completed" ? "var(--g50)" : "#f5f5f5",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {s.status === "completed"
                    ? <CheckCircle2 size={18} color="var(--g600)" strokeWidth={2} />
                    : <Building2 size={16} color="var(--mut)" />
                  }
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)", marginBottom: 3 }}>
                    {propName}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--mut)" }}>
                    {formatDate(s.service_date)}
                    {s.updated_at && <span> · {formatTime(s.updated_at)}</span>}
                  </div>
                </div>

                <StatusBadge status={s.status} />

                {isFirst && (
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
                    background: "#e65100", color: "#fff",
                    textTransform: "uppercase", letterSpacing: ".5px", flexShrink: 0,
                  }}>Latest</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <style>{`@keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </DashboardLayout>
  );
}